/**
 * TeacherAvailability (BON-241) — "Declare when you can work: a recurring
 * weekly rhythm plus specific-date exceptions."
 *
 * The design offers two input modes and explicitly recommends this one (set
 * the rhythm once, patch one-off days) over painting a calendar, because
 * painting hides the "this repeats forever" contract.
 *
 * Everything is a local draft until "Save availability" — one PUT replaces
 * the rhythm and future exceptions together, matching the single-button
 * design. Past-dated exceptions live server-side and are never sent back.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchOwnAvailability, saveOwnAvailability } from '../../../api/availability';
import { BucketError, BucketLoading, SectionCard } from '../regional/BucketStates';

import { AvailabilityExceptions } from './AvailabilityExceptions';
import { draftToRhythm, summarizeRhythm } from './availabilityModel';
import { AvailabilityRhythmTable } from './AvailabilityRhythmTable';
import { useAvailabilityDraft } from './useAvailabilityDraft';

export const AVAILABILITY_KEY = ['me.availability'];

function FirstRunHint() {
  return (
    <p style={{ margin: 0, fontSize: 12, color: 'var(--nb-subtle)' }}>
      Tell us the days and times you can teach. Your director uses this to offer you shifts that
      actually fit — you’ll only ever see shifts you’re free for.
    </p>
  );
}

function SaveBar({
  dirty,
  saving,
  justSaved,
  onSave,
}: {
  dirty: boolean;
  saving: boolean;
  justSaved: boolean;
  onSave: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button
        type="button"
        className="nb-btn nb-btn-primary"
        disabled={!dirty || saving}
        onClick={onSave}
      >
        {saving ? 'Saving…' : 'Save availability'}
      </button>
      {dirty && !saving && (
        <span style={{ fontSize: 11.5, color: 'var(--nb-subtle)' }}>Unsaved changes</span>
      )}
      {!dirty && justSaved && (
        <span role="status" style={{ fontSize: 11.5, color: 'var(--nb-primary)' }}>
          Saved
        </span>
      )}
    </div>
  );
}

export default function TeacherAvailability() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: AVAILABILITY_KEY,
    queryFn: fetchOwnAvailability,
    staleTime: 30_000,
  });
  const draft = useAvailabilityDraft(q.data);

  const save = useMutation({
    mutationFn: saveOwnAvailability,
    onSuccess: (next) => {
      qc.setQueryData(AVAILABILITY_KEY, next);
      // The RD grid reads the same declarations.
      qc.invalidateQueries({ queryKey: ['dashboards.regional'] });
      draft.markSaved();
    },
  });

  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;

  const summary = summarizeRhythm(draft.rhythm);

  return (
    <div style={{ display: 'grid', gap: 14, maxWidth: 640 }}>
      <SectionCard
        title="Weekly rhythm"
        action={
          summary ? (
            <span style={{ fontSize: 11.5, color: 'var(--nb-primary)' }}>{summary}</span>
          ) : undefined
        }
      >
        {q.data.updatedAtIso === null && <FirstRunHint />}
        <AvailabilityRhythmTable
          draft={draft.rhythm}
          disabled={save.isPending}
          onChange={draft.setSlot}
        />
      </SectionCard>

      <SectionCard title="Specific dates">
        <AvailabilityExceptions
          exceptions={draft.exceptions}
          disabled={save.isPending}
          onAdd={draft.addException}
          onRemove={draft.removeException}
        />
      </SectionCard>

      {save.isError && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--nb-warn)' }}>
          {(save.error as Error).message}
        </div>
      )}

      <SaveBar
        dirty={draft.dirty}
        saving={save.isPending}
        justSaved={draft.justSaved}
        onSave={() =>
          save.mutate({ rhythm: draftToRhythm(draft.rhythm), exceptions: draft.exceptions })
        }
      />
    </div>
  );
}
